import type { LoadedBcf } from '@modules/bcf/store/bcfClientSlice'
import { markWorkDirty } from '@core/session/recovery'

const DB_NAME = 'vn2r-bcf'
const DB_VERSION = 1
const STORE = 'loaded'

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'localId' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error ?? new Error('IDB open failed'))
    }
  })
  return dbPromise
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    let result: T
    Promise.resolve(fn(store))
      .then((r) => {
        result = r as T
      })
      .catch(reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error ?? new Error('IDB tx failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'))
  })
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB req failed'))
  })
}

export const bcfPersistence = {
  async saveAll(entries: LoadedBcf[]): Promise<void> {
    await withStore('readwrite', async (s) => {
      await reqToPromise(s.clear())
      for (const e of entries) {
        await reqToPromise(s.put(e))
      }
    })
    markWorkDirty()
  },

  async save(entry: LoadedBcf): Promise<void> {
    await withStore('readwrite', (s) => reqToPromise(s.put(entry)))
    markWorkDirty()
  },

  async remove(localId: string): Promise<void> {
    await withStore('readwrite', (s) => reqToPromise(s.delete(localId)))
    markWorkDirty()
  },

  async loadAll(): Promise<LoadedBcf[]> {
    return withStore('readonly', (s) => reqToPromise(s.getAll() as IDBRequest<LoadedBcf[]>))
  },
}
